import { faCloudUploadAlt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useAlertService from "@mirage/hooks/useAlertService";
import { convertFileToBase64 } from "@mirage/utilities/ConvertToBase64";
import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { FileContent } from "@shared/mirage/core/contracts/UploadedFile";
import React, { memo, useEffect } from "react";
import { useDrop, DropTargetMonitor } from "react-dnd";
import { NativeTypes } from "react-dnd-html5-backend";
import "./DragAndDropFiles.css";

interface DropWrapperProps {
    onDrop: (files: FileContent[]) => void;
    inputId: string;
    onClick?: (files: FileContent[]) => void;
    wrapperClassName?: string;
    wrapperIsOverClassName?: string;
    children?: React.ReactNode;
    label?: string;
    iconHidden?: boolean;
    multiple?: boolean;
    readonly?: boolean;
}

const DropWrapper: React.FC<DropWrapperProps> = memo(({ inputId, onClick, onDrop, children, label, multiple, iconHidden, wrapperClassName, wrapperIsOverClassName, readonly }) => {
    const showAlert = useAlertService();
    useEffect(() => {
        const inputById = document.querySelectorAll(`#${inputId}`);
        if (inputById.length > 1) {
            throw Error(`DragAndDropFiles >> Duplicated IDs for input ${inputId}`);
        }
    }, [inputId]);

    const [{ isOver }, drop] = useDrop(
        () => ({
            accept: [NativeTypes.FILE],
            canDrop: (item: { files: File[] }) => {
                if (readonly) {
                    return false;
                }
                if (!multiple && item.files.length > 1) {
                    showAlert("Only one file is allowed.", "error");
                    return false;
                }
                return true;
            },
            async drop(item: { files: File[] }) {
                if (onDrop) {
                    const filesWithBase64 = await handleDropFile(item.files);
                    onDrop(filesWithBase64);
                }
            },
            collect: (monitor: DropTargetMonitor) => {
                return {
                    isOver: monitor.isOver(),
                    canDrop: monitor.canDrop(),
                };
            },
        }),
        [onDrop]
    );
    const wrapIsOverClassName = isOver ? (wrapperIsOverClassName ? wrapperIsOverClassName : "filesDropZone--hover") : "";
    const wrapClassName = wrapperClassName ? wrapperClassName : "filesDropZone";
    const wrapIsOverWithDefaultClassName = `${wrapClassName} ${wrapIsOverClassName}`.trim();

    const dropZoneText = label ? (
        <Typography variant="body2" className="filesDropZone__text">
            {label}
        </Typography>
    ) : null;

    return (
        <Box ref={drop} className={wrapIsOverWithDefaultClassName} onClick={attachmentsOnClickHandler}>
            {children}
            <input multiple={multiple} type="file" id={`${inputId}`} className="filesDropZone__fileAppendInput"></input>
            {!iconHidden ? <FontAwesomeIcon icon={faCloudUploadAlt} className="filesDropZone__icon" /> : null}
            {dropZoneText}
        </Box>
    );

    async function handleDropFile(files: File[] | File): Promise<FileContent[]> {
        if (Array.isArray(files)) {
            const convertedFiles = await Promise.all(files.map(async (file) => await convertFile(file)));
            return convertedFiles.filter((file) => file?.name && file.base64) as FileContent[];
        }
        const convertedFile = await convertFile(files);
        return convertedFile?.base64 ? ([convertedFile] as FileContent[]) : [];
    }

    function attachmentsOnClickHandler() {
        const input = document.getElementById(`${inputId}`) as HTMLInputElement;
        if (input && onClick) {
            input.onchange = async () => {
                if (multiple) {
                    const files = input.files?.length ? Array.from(input.files) : [];
                    const convertedFiles = await Promise.all(files.map(async (file) => await convertFile(file)));
                    const filesWithBase64: FileContent[] = convertedFiles.filter((file) => file?.name && file.base64) as FileContent[];
                    onClick(filesWithBase64);
                } else {
                    const file = input.files?.item(0);
                    const convertedFile = file && (await convertFile(file));
                    const filesWithBase64 = convertedFile?.base64 ? ([convertedFile] as FileContent[]) : [];
                    onClick(filesWithBase64);
                }
            };
            input.click();
        }
    }

    async function convertFile(file: File) {
        let base64File = {} as FileContent;
        const base64 = await convertFileToBase64(file);
        if (base64 && typeof base64 === "string") {
            base64File = { ...file, name: file.name, base64: base64.slice(base64.indexOf(",") + 1) };
            return base64File;
        }
        showAlert(`Could not upload file - ${file.name}`, "error");
        return null;
    }
});

export default DropWrapper;
